import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, Calendar, Check, ChevronsUpDown, FileDown } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useLibroMayor } from "~/hooks/contabilidad/useLibroMayor";
import { exportBalance8ColumnasToExcel } from "~/services/contabilidad/balance8ColumnasExportService";

import { Balance8ColumnasTable } from "./components/Balance8ColumnasTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

// Generate month options for the last 24 months
function generateMonthOptions() {
  const options = [];
  const now = dayjs();
  for (let i = 0; i < 24; i++) {
    const date = now.subtract(i, "month");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
    });
  }
  return options;
}

export default function Balance8ColumnasPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Calculate date range from selected month
  const dateFrom = dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD");
  const dateTo = dayjs(selectedMonth).endOf("month").format("YYYY-MM-DD");

  const { data: companies = [] } = useCompanies();
  const { data: accounts = [] } = useAccounts();

  // Filter accounts by selected company
  const filteredAccounts = useMemo(() => {
    if (!companyFilter) return [];
    return accounts.filter(
      (a) => a.company_id === companyFilter && a.is_active
    );
  }, [accounts, companyFilter]);

  const { data: lines = [], isLoading } = useLibroMayor({
    dateFrom,
    dateTo,
    companyId: companyFilter,
    accountId: accountFilter === "all" ? undefined : accountFilter,
  });

  // Reset account filter when company changes
  const handleCompanyChange = (value: string) => {
    setCompanyFilter(value);
    setAccountFilter("all");
    setAccountSearchOpen(false);
  };

  // Handle export
  const handleExport = () => {
    if (lines.length === 0) return;

    setIsExporting(true);
    try {
      const selectedCompany = companies.find((c) => c.id === companyFilter);
      const periodLabel = dayjs(selectedMonth).format("MMMM YYYY");

      exportBalance8ColumnasToExcel({
        lines,
        period: periodLabel,
        companyName: selectedCompany?.name || "",
      });

      toast({
        title: "Reporte exportado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting Balance 8 Columnas:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && !companyFilter) {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      } else if (companies.length === 1) {
        setCompanyFilter(companies[0].id);
      }
    }
  }, [companies]);

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/contabilidad/reportes")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Balance 8 Columnas</h1>
            <p className="text-sm text-muted-foreground">
              Balance de comprobacion con saldo inicial, movimientos y saldo final
            </p>
          </div>
        </div>
        <Button
          onClick={handleExport}
          disabled={lines.length === 0 || isLoading || isExporting}
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Periodo Contable</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccionar periodo" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Empresa <span className="text-red-500">*</span>
              </Label>
              <Select value={companyFilter} onValueChange={handleCompanyChange}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Cuenta</Label>
              <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={accountSearchOpen}
                    className="w-[250px] justify-between rounded-xl font-normal"
                    disabled={!companyFilter}
                  >
                    {accountFilter === "all"
                      ? "Todas las cuentas"
                      : filteredAccounts.find((a) => a.id === accountFilter)
                        ? `${filteredAccounts.find((a) => a.id === accountFilter)?.code} - ${filteredAccounts.find((a) => a.id === accountFilter)?.name}`
                        : "Seleccionar cuenta"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar cuenta..." />
                    <CommandList>
                      <CommandEmpty>No se encontro cuenta.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setAccountFilter("all");
                            setAccountSearchOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              accountFilter === "all" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Todas las cuentas
                        </CommandItem>
                        {filteredAccounts.map((account) => (
                          <CommandItem
                            key={account.id}
                            value={`${account.code} ${account.name}`}
                            onSelect={() => {
                              setAccountFilter(account.id);
                              setAccountSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                accountFilter === account.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {account.code} - {account.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
      </Card>

      {!companyFilter ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">
              Selecciona una empresa para ver el Balance 8 Columnas.
            </p>
            <p className="text-sm text-muted-foreground">
              La empresa es un campo requerido para este reporte.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Detalle por Cuenta</CardTitle>
            <p className="text-sm text-muted-foreground">
              {lines.length} cuentas con movimientos
            </p>
          </CardHeader>
          <CardContent>
            <Balance8ColumnasTable lines={lines} isLoading={isLoading} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
